import React from 'react'
import type { Section } from '../../types/teamContentPage.types'
import { FileText, Image, Download, Calendar } from 'lucide-react'
import { EventsSection } from './EventsSection'
import '../../styles/sections.scss'

interface TeamSectionRendererProps {
  section: Section
}

export const TeamSectionRenderer: React.FC<TeamSectionRendererProps> = ({ section }) => {
  const renderSectionContent = () => {
    switch (section.typeOf) {
      case 'details':
      case 'text':
        return (
          <div className="prose max-w-none overflow-hidden">
            {section.details && (
              <div 
                className="w-full overflow-x-auto [&_iframe]:max-w-full [&_iframe]:h-auto [&_iframe]:aspect-video [&_img]:max-w-full [&_img]:h-auto"
                dangerouslySetInnerHTML={{ __html: section.details }} 
              />
            )}
          </div>
        )

      case 'photos':
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {section.attachments?.map((attachment) => (
              <div key={attachment.id} className="relative group">
                <img
                  src={attachment.fileUrl}
                  alt={attachment.name}
                  className="w-full h-48 object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg" />
              </div>
            ))}
          </div>
        )

      case 'files':
        return (
          <div className="space-y-2">
            {section.attachments?.map((attachment) => (
              <a
                key={attachment.id}
                href={attachment.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Download className="h-5 w-5 text-gray-400" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{attachment.name}</p>
                  <p className="text-sm text-gray-500">
                    {(attachment.fileFileSize / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </a>
            ))}
          </div>
        )

      case 'events':
        return <EventsSection section={section} />

      default:
        return null
    }
  }

  // Get border style based on section options
  const getBorderStyle = () => {
    switch (section.sectionOption?.borderStyle) {
      case 'none': return 'border-0'
      case 'square': return 'rounded-none'
      default: return 'rounded-lg'
    }
  }

  // Get padding based on section options
  const getPadding = () => {
    const padding = section.sectionOption?.padding
    if (padding === 'none') return 'p-0'
    if (padding === 'small') return 'p-3'
    if (padding === 'large') return 'p-8'
    return 'p-6' // default
  }

  const containerClass = section.sectionOption?.fullWidth
    ? 'w-full'
    : 'max-w-4xl mx-auto'

  // Only apply card styling for non-text sections
  if (section.typeOf === 'text') {
    return (
      <div className={containerClass}>
        {section.customTitle && (
          <h3 className="text-xl font-semibold mb-4">{section.customTitle}</h3>
        )}
        {renderSectionContent()}
      </div>
    )
  }

  // For all other sections, apply white card background
  return (
    <div 
      className={`bg-white shadow-sm border border-gray-100 ${getBorderStyle()} ${getPadding()}`}
      style={{
        backgroundColor: section.sectionOption?.backgroundColor || undefined,
        color: section.sectionOption?.textColor || undefined
      }}
    >
      <div className={containerClass}>
        {section.customTitle && (
          <h3 className="text-xl font-semibold mb-4">{section.customTitle}</h3>
        )}
        {renderSectionContent()}
      </div>
    </div>
  )
}